import React, { useState, useEffect } from 'react';
import { 
  Server, 
  Laptop, 
  Clock, 
  ShieldCheck, 
  Activity, 
  Cpu, 
  HardDrive, 
  ArrowUpRight,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import { InfraHealth } from '../types';

interface InfraHealthBarProps {
  health: InfraHealth;
}

export const InfraHealthBar: React.FC<InfraHealthBarProps> = ({ health }) => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatUtcTime = (d: Date) => {
    return d.toISOString().substring(11, 19) + ' UTC';
  };

  const formatLocalTime = (d: Date) => {
    return d.toLocaleTimeString([], { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const isVpsHealthy = health.vpsStatus === 'HEALTHY';
  const isMacOnline = health.macStatus === 'ONLINE';

  return (
    <div className="bg-slate-900/60 border-b border-slate-800/80 px-4 sm:px-6 lg:px-8 py-2.5 text-xs font-mono">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-2.5 gap-x-6">
        
        {/* Left: Infrastructure Node Badges */}
        <div className="flex flex-wrap items-center gap-3">
          
          {/* VPS Gateway Slot */}
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-800/80 border border-slate-700/60 hover:border-cyan-500/40 transition-colors">
            <span className="flex h-2 w-2 relative">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isVpsHealthy ? 'bg-cyan-400' : 'bg-amber-400'} opacity-75`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isVpsHealthy ? 'bg-cyan-500' : 'bg-amber-500'}`}></span>
            </span>
            <div className="flex items-center gap-1.5">
              <Server className="w-3.5 h-3.5 text-cyan-400" />
              <span className="font-semibold text-slate-200">VPS Gateway</span>
              <span className="text-[10px] px-1 py-0.2 rounded bg-cyan-950/60 text-cyan-300 border border-cyan-800/60">
                {health.vpsRegion}
              </span>
            </div>
            <div className="flex items-center gap-2 pl-1 border-l border-slate-700 text-[11px] text-slate-400">
              <span className="text-cyan-300 font-semibold">{health.vpsLatencyMs}ms</span>
              <span className="text-slate-500">|</span>
              <span title="CPU Load" className="flex items-center gap-0.5 text-slate-300">
                <Cpu className="w-3 h-3 text-slate-400" />
                {health.vpsCpuLoad}%
              </span>
            </div>
          </div>

          {/* Mac Agent Connectivity via Tailscale */}
          <div className="flex items-center gap-2 px-2.5 py-1 rounded-md bg-slate-800/80 border border-slate-700/60 hover:border-purple-500/40 transition-colors">
            <span className="flex h-2 w-2 relative">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${isMacOnline ? 'bg-purple-400' : 'bg-rose-400'} opacity-75`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${isMacOnline ? 'bg-purple-500' : 'bg-rose-500'}`}></span>
            </span>
            <div className="flex items-center gap-1.5">
              <Laptop className="w-3.5 h-3.5 text-purple-400" />
              <span className="font-semibold text-slate-200">Mac Agent (Tailscale)</span>
              <span className="text-[10px] px-1 py-0.2 rounded bg-purple-950/60 text-purple-300 border border-purple-800/60">
                {health.macTailscaleIp}
              </span>
            </div>
            <div className="flex items-center gap-2 pl-1 border-l border-slate-700 text-[11px] text-slate-400">
              <span className="text-purple-300 font-semibold">{health.macLatencyMs}ms</span>
              <span className="text-slate-500">|</span>
              <span className="text-slate-300 hidden sm:inline" title={health.macOsVersion}>
                M3 Max
              </span>
            </div>
          </div>

        </div>

        {/* Right: Live Clock & Uptime Indicator */}
        <div className="flex items-center gap-4 text-slate-400 ml-auto lg:ml-0">
          
          {/* Global Uptime */}
          <div className="flex items-center gap-1.5" title="Cluster aggregate availability">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-slate-400 hidden sm:inline">Uptime:</span>
            <span className="text-emerald-300 font-semibold">{health.uptimePercent}%</span>
            <span className="text-slate-500 text-[10px]">({health.uptimeString})</span>
          </div>

          <span className="text-slate-700">•</span>

          {/* Live UTC & Local Clock */}
          <div className="flex items-center gap-2 text-[11px] text-slate-300">
            <Clock className="w-3.5 h-3.5 text-slate-400" />
            <span className="font-medium text-slate-200">{formatLocalTime(currentTime)}</span>
            <span className="text-slate-500 text-[10px]">({formatUtcTime(currentTime)})</span>
          </div>

        </div>

      </div>
    </div>
  );
};
